// src/utils/navigation-debug.ts

/**
 * Script de débogage pour les problèmes de navigation
 * À utiliser pour diagnostiquer les problèmes de navigation
 */

interface NavigationInfo {
  currentPath: string;
  hasTrailingSlash: boolean;
  baseUrl: string;
  isProd: boolean;
}

/**
 * Affiche les informations de débogage pour la navigation
 * Peut être appelé dans la console du navigateur: window.debugNavigation()
 */
export function debugNavigation(): NavigationInfo {
  const info: NavigationInfo = {
    currentPath: window.location.pathname,
    hasTrailingSlash: window.location.pathname.endsWith('/'),
    baseUrl: window.location.origin,
    isProd: import.meta.env.PROD
  };
  
  console.group('Informations de navigation');
  console.log('URL complète:', window.location.href);
  console.log('Chemin actuel:', info.currentPath);
  console.log('URL de base:', info.baseUrl);
  console.log('Environnement de production:', info.isProd);
  console.log('Paramètres URL:', window.location.search);
  console.log('Fragment URL:', window.location.hash);
  console.groupEnd();
  
  return info;
}

/**
 * Vérifie si les liens sur la page sont correctement formés
 * Peut être appelé dans la console du navigateur: window.checkLinks()
 */
export function checkLinks(): void {
  const links = document.querySelectorAll('a');
  
  console.group('Vérification des liens');
  console.log(`Nombre total de liens: ${links.length}`);
  
  const brokenLinks: { element: HTMLAnchorElement; href: string; reason: string }[] = [];
  
  links.forEach((link: HTMLAnchorElement) => {
    const href = link.getAttribute('href');
    
    // Pas d'attribut href
    if (!href) {
      brokenLinks.push({
        element: link,
        href: 'Aucun',
        reason: 'Aucun attribut href'
      });
      return;
    }
    
    // Liens externes ou absolus
    if (href.startsWith('http') || href.startsWith('https') || href.startsWith('//')) {
      return;
    }
    
    // Ancres seulement
    if (href.startsWith('#')) {
      const targetId = href.substring(1);
      const target = document.getElementById(targetId);
      
      if (!target) {
        brokenLinks.push({
          element: link,
          href,
          reason: `Ancre (#${targetId}) non trouvée dans la page`
        });
      }
      return;
    }
    
    // Vérifications pour les chemins relatifs
    if (href.startsWith('./') || href.startsWith('../') || (!href.startsWith('/') && !href.includes('://'))) {
      if (!href.endsWith('.html') && !href.endsWith('/') && !href.includes('#')) {
        brokenLinks.push({
          element: link,
          href,
          reason: 'Chemin relatif sans extension .html'
        });
      }
    }
  });
  
  if (brokenLinks.length > 0) {
    console.warn(`${brokenLinks.length} liens potentiellement problématiques trouvés:`);
    brokenLinks.forEach((item, index) => {
      console.log(`${index + 1}. "${item.element.textContent}" (${item.href}): ${item.reason}`);
      console.log(item.element);
    });
  } else {
    console.log('Aucun problème de lien détecté');
  }
  
  console.groupEnd();
}

// Exposition globale pour utilisation dans la console
declare global {
  interface Window {
    debugNavigation: () => NavigationInfo;
    checkLinks: () => void;
  }
}

// Si ce fichier est importé dans main.ts, ces fonctions seront accessibles globalement
window.debugNavigation = debugNavigation;
window.checkLinks = checkLinks;

export default {
  debugNavigation,
  checkLinks
};
